import React, { useState, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, AppDispatch } from '@/store'
import {
  sendMessage,
  updateInputValue,
  showSuggestions,
  hideSuggestions,
  generateSuggestions,
  setTyping
} from '@/store/slices/ragChatSlice'
import { 
  PaperAirplaneIcon,
  MicrophoneIcon,
  PlusIcon,
  PhotoIcon
} from '@heroicons/react/24/outline'

export const ChatInput: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const {
    inputValue,
    isLoading,
    currentSession,
    currentContext,
    suggestions
  } = useSelector((state: RootState) => state.ragChat)
  
  const [isFocused, setIsFocused] = useState(false)
  const [isComposing, setIsComposing] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const maxRows = 4

  // 自動調整文字區域高度
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      const scrollHeight = textarea.scrollHeight
      const lineHeight = 20 // 假設行高為 20px
      const maxHeight = lineHeight * maxRows
      textarea.style.height = Math.min(scrollHeight, maxHeight) + 'px'
      textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden'
    }
  }, [inputValue])

  // 處理輸入變更
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch(updateInputValue(e.target.value))
    
    // 如果有輸入且有建議，顯示建議
    if (e.target.value.trim() && suggestions.length > 0) {
      dispatch(showSuggestions())
    } else {
      dispatch(hideSuggestions())
    }
  }

  // 處理發送訊息
  const handleSendMessage = async () => {
    const message = inputValue.trim()
    if (!message || !currentSession || isLoading) return

    // 顯示打字指示器
    dispatch(setTyping(true))
    
    try {
      await dispatch(sendMessage({
        message,
        sessionId: currentSession.id,
        context: currentContext || undefined,
        mode: 'smart'
      })).unwrap()
    } catch (error) {
      console.error('發送訊息失敗:', error)
    } finally {
      dispatch(setTyping(false))
    }
  }

  // 處理鍵盤事件
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
      e.preventDefault()
      handleSendMessage()
    }
    
    // ESC 鍵隱藏建議
    if (e.key === 'Escape') {
      dispatch(hideSuggestions())
    }
  }

  // 處理焦點事件
  const handleFocus = () => {
    setIsFocused(true)
    // 生成上下文相關的建議
    if (currentContext && suggestions.length === 0) {
      dispatch(generateSuggestions(currentContext))
    }
  }

  const handleBlur = () => {
    setIsFocused(false)
    // 延遲隱藏建議，允許點擊建議
    setTimeout(() => {
      dispatch(hideSuggestions())
    }, 200)
  }

  // 處理組合輸入（中文輸入法）
  const handleCompositionStart = () => {
    setIsComposing(true)
  }

  const handleCompositionEnd = () => {
    setIsComposing(false)
  }

  // 處理貼上事件
  const handlePaste = (e: React.ClipboardEvent) => {
    // 如果貼上的是圖片，可以考慮支援圖片查詢
    const items = e.clipboardData.items
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        // TODO: 實作圖片上傳和多模態查詢
        console.log('檢測到圖片貼上，未來可支援圖片查詢')
        break
      }
    }
  }

  const canSend = inputValue.trim() && !isLoading && currentSession

  return (
    <div className="p-4">
      {/* 輸入區域 */}
      <div className={`
        relative rounded-lg border-2 transition-colors
        ${isFocused ? 'border-blue-500' : 'border-gray-200'}
        ${isLoading ? 'opacity-75' : ''}
      `}>
        <div className="flex items-end space-x-2 p-3">
          {/* 附加功能按鈕 */}
          <div className="flex space-x-1 pb-1">
            <button
              className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
              title="添加附件"
              disabled={isLoading}
            >
              <PlusIcon className="h-4 w-4" />
            </button>
            
            <button
              className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
              title="上傳圖片"
              disabled={isLoading}
            >
              <PhotoIcon className="h-4 w-4" />
            </button>
            
            <button
              className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
              title="語音輸入"
              disabled={isLoading}
            >
              <MicrophoneIcon className="h-4 w-4" />
            </button>
          </div>

          {/* 文字輸入區域 */}
          <div className="flex-1">
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleCompositionEnd}
              onPaste={handlePaste}
              placeholder={isLoading ? "AI 正在回應中..." : "輸入您的問題... (Shift+Enter 換行)"}
              className="w-full resize-none border-none outline-none text-sm leading-5 max-h-20"
              rows={1}
              disabled={isLoading}
            />
          </div>

          {/* 發送按鈕 */}
          <button
            onClick={handleSendMessage}
            disabled={!canSend}
            className={`
              p-2 rounded-lg transition-all duration-200 flex-shrink-0
              ${canSend
                ? 'bg-blue-600 hover:bg-blue-700 text-white transform hover:scale-105'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }
            `}
            title="發送訊息 (Enter)"
          >
            <PaperAirplaneIcon className="h-4 w-4" />
          </button>
        </div>

        {/* 載入指示器 */}
        {isLoading && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-blue-100 rounded-t-lg overflow-hidden">
            <div className="h-full bg-blue-600 rounded-t-lg animate-pulse" />
          </div>
        )}
      </div>

      {/* 底部提示 */}
      <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
        <div className="flex items-center space-x-4">
          <span>AI 由 RAG 技術驱动</span>
          {currentContext?.currentEntity && (
            <span className="flex items-center">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-1" />
              上下文: {currentContext.currentEntity.type}
            </span>
          )}
        </div>
        
        <div className="text-right">
          {inputValue.length > 0 && (
            <span className={inputValue.length > 500 ? 'text-orange-500' : ''}>
              {inputValue.length}/1000
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export default ChatInput
